import { IMGS_PATH } from 'constants/image';
import { PaymentMethod } from 'constants/payment';
import * as Sentry from '@sentry/react';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import ReactCSSTransitionReplace from 'react-css-transition-replace';
import Button from 'components/Qds/Button';
import ButtonGroup from 'components/Qds/ButtonGroup';
import Alert from 'components/ui/Alert';
import Image from 'components/ui/Image';
import { useAnalytics } from 'hooks/useAnalytics';
import { useTranslation } from 'hooks/useTranslation';
import PaymentFormStep3 from './PaymentFormStep3';
import PaymentFormStep2 from './PaymentFormStep2';
import PaymentFormStep1 from './PaymentFormStep1';
import styles from './PaymentForm.module.scss';
import type {
  PaymentFormStepBaseProps,
  PaymentInformationPayload,
} from 'types/Payment';
import type { MouseEvent } from 'react';

type PaymentFormStepProps = {
  step: number;
} & PaymentFormStepBaseProps;

const PaymentFormStep = ({
  errors,
  formData,
  method,
  register,
  step,
}: PaymentFormStepProps): JSX.Element => {
  switch (step) {
    case 1:
      return <PaymentFormStep2 method={method} formData={formData} />;
    case 2:
      return <PaymentFormStep3 />;
    default:
      return (
        <PaymentFormStep1
          method={method}
          formData={formData}
          register={register}
          errors={errors}
        />
      );
  }
};

type Props = {
  onSubmit: (id: string, payload: PaymentInformationPayload) => void;
  page: string;
};

let formData: Record<string, string> = {};
const PaymentForm = ({ onSubmit, page }: Props): JSX.Element => {
  const { t } = useTranslation();
  const { trackEvent } = useAnalytics();
  const { query } = useRouter();
  const {
    clearErrors,
    errors,
    formState,
    getValues,
    handleSubmit,
    register,
    trigger,
  } = useForm({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  });

  const [direction, setDirection] = useState(0);
  const [activeStep, setActiveStep] = useState(0);
  const [autoFocus, setAutoFocus] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<string>(
    PaymentMethod.BANK,
  );
  const steps = 2;
  const translateStep = activeStep + 1;
  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === steps;

  const stepForward = async (event: MouseEvent<HTMLElement>): Promise<void> => {
    if (event.type === 'click') {
      event.preventDefault();
    }

    const isValid = await trigger();
    if (!isValid) {
      return;
    }

    trackEvent({
      category: `funnel|${page}`,
      action: 'funnel|Click',
      label: 'funnel|Step1|Next',
    });

    const next = Math.min(activeStep + 1, steps);
    formData = { ...formData, ...getValues() };
    prepareNextStep(next, 1, event.detail === 0);
  };

  const stepBack = (event: MouseEvent<HTMLElement>): void => {
    trackEvent({
      category: `funnel|${page}`,
      action: 'funnel|Click',
      label: 'funnel|Step2|Back',
    });

    const prev = Math.max(activeStep - 1, 0);
    prepareNextStep(prev, -1, event.detail === 0);
  };

  const prepareNextStep = (
    next: number,
    directionNumber: number,
    keyboardTriggered: boolean,
  ): void => {
    if (directionNumber !== direction) {
      setDirection(directionNumber);
    }

    setActiveStep(next);
    if (keyboardTriggered !== autoFocus) {
      setAutoFocus(keyboardTriggered);
    }
  };

  const doSubmit = async (): Promise<void> => {
    const id = query?.id || null;

    // eslint-disable-next-line
    // @ts-ignore
    const payload: PaymentInformationPayload = {
      ...formData,
      preference: formData.paypalEmail
        ? PaymentMethod.PAYPAL
        : PaymentMethod.BANK,
      name: formData['accountHolderName'],
    };

    if (id) {
      trackEvent({
        category: `funnel|${page}`,
        action: 'funnel|Click',
        label: 'funnel|Step2|Confirm',
      });

      try {
        const response = await onSubmit(id as string, payload);
        // eslint-disable-next-line
        // @ts-ignore
        if (response.success) {
          return setActiveStep(2);
        }
      } catch (error) {
        const { message, stack } = error as Error;
        Sentry.captureException(new Error("Can't submit payment form"), {
          level: 'fatal' as Sentry.SeverityLevel,
          extra: {
            error: message,
            stack,
          },
        });
      }
    } else {
      Sentry.captureMessage('No id was provided.', {
        level: 'warning' as Sentry.SeverityLevel,
        extra: {
          source: 'payment form',
        },
      });
    }
  };

  return (
    <div className={styles.formContainer}>
      <div className={styles.formWrapper}>
        <div className={styles.formGrid}>
          <div className={styles.formGridInner}>
            <div className={styles.descriptionContainer}>
              <div className={styles.descriptionWrapper}>
                <p className={styles.headline}>
                  {t(`referAFriend.rewardForm.step${translateStep}.headline`)}
                </p>
                <p className={styles.description}>
                  {t(
                    `referAFriend.rewardForm.step${translateStep}.description`,
                  )}
                </p>
                <div className={styles.trustImages}>
                  <div className={styles.trustImagesWrapper}>
                    <Image
                      src={`${IMGS_PATH.PUBLIC}/norton-internet-security.jpg`}
                      alt="Norton Internet Security"
                      width={80}
                      height={44}
                      className={styles.trustImagesNorton}
                    />
                  </div>
                  <div className={styles.trustImagesWrapper}>
                    <Image
                      src={`${IMGS_PATH.PUBLIC}/trust-lock-badge.png`}
                      alt="TrustLock"
                      width={96}
                      height={36}
                      className={styles.trustImagesTrustLock}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.formGridInner}>
            {!query.id ? (
              <Alert
                type="generic"
                text="There was no quno id provided."
                vertical={false}
              />
            ) : (
              <form autoComplete="off" onSubmit={handleSubmit(doSubmit)}>
                <div className={styles.paymentForm}>
                  {isFirstStep && (
                    <ul className={styles.methodSelection}>
                      <li
                        className={clsx(
                          styles.methodSelectionItem,
                          paymentMethod === PaymentMethod.BANK &&
                            styles.methodSelectionItemSelected,
                        )}
                        onClick={() => {
                          clearErrors();
                          setPaymentMethod(PaymentMethod.BANK);
                        }}
                      >
                        {t('referAFriend.rewardForm.bankDetails')}
                      </li>
                      <li
                        className={clsx(
                          styles.methodSelectionItem,
                          paymentMethod === PaymentMethod.PAYPAL &&
                            styles.methodSelectionItemSelected,
                        )}
                        onClick={() => {
                          clearErrors();
                          setPaymentMethod(PaymentMethod.PAYPAL);
                        }}
                      >
                        <Image
                          className="mx-auto"
                          src={`${IMGS_PATH.PUBLIC}/paypal.png`}
                          alt={t('referAFriend.rewardForm.paypal')}
                          width={80}
                          height={21}
                        />
                      </li>
                    </ul>
                  )}
                  <ReactCSSTransitionReplace
                    transitionName="inRightOutLeft"
                    transitionEnterTimeout={500}
                    transitionLeaveTimeout={500}
                  >
                    <PaymentFormStep
                      step={activeStep}
                      method={paymentMethod}
                      formData={formData}
                      register={register}
                      errors={errors}
                    />
                  </ReactCSSTransitionReplace>
                </div>
                <ul
                  className={clsx(
                    styles.dotsContainer,
                    isLastStep && 'invisible',
                  )}
                >
                  {Array.from({ length: 3 }).map((_, i) => (
                    <li
                      key={`step_dot_${i}`}
                      className={clsx(
                        styles.dots,
                        activeStep === i && styles.dotsActive,
                      )}
                    >
                      <span>{i + 1}</span>
                    </li>
                  ))}
                </ul>
                <ButtonGroup orientation="horizontal">
                  {activeStep === 1 && (
                    <Button
                      disabled={formState.isSubmitting}
                      className={clsx(
                        formState.isSubmitting && styles.notAllowed,
                      )}
                      fullWidth
                      variant="secondary"
                      onClick={stepBack}
                    >
                      {t('referAFriend.rewardForm.back')}
                    </Button>
                  )}
                  {!isLastStep && (
                    <Button
                      disabled={formState.isSubmitting}
                      className={clsx(
                        formState.isSubmitting && styles.notAllowed,
                      )}
                      type={isFirstStep ? 'button' : 'submit'}
                      onClick={isFirstStep ? stepForward : undefined}
                      fullWidth
                    >
                      {isFirstStep
                        ? t('referAFriend.rewardForm.next')
                        : t('referAFriend.rewardForm.confirm')}
                    </Button>
                  )}
                </ButtonGroup>
              </form>
            )}
          </div>
        </div>
        <div className={styles.trustImagesMobile}>
          <div className={styles.trustImagesWrapper}>
            <Image
              src={`${IMGS_PATH.PUBLIC}/norton-internet-security.jpg`}
              alt="Norton Internet Security"
              width={80}
              height={44}
              className={styles.trustImagesNorton}
            />
          </div>
          <div className={styles.trustImagesWrapper}>
            <Image
              src={`${IMGS_PATH.PUBLIC}/trust-lock-badge.png`}
              alt="TrustLock"
              width={96}
              height={36}
              className={styles.trustImagesTrustLock}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentForm;
